import { QueryBuilder } from "@cgarciagarcia/react-query-builder";
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  createBanner,
  deleteBanner,
  getBanner,
  listBanners,
  toggleBanner,
  updateBanner,
} from "~/api/banner";
import { CreateBannerPayload, UpdateBannerPayload } from "~/api/banner.types";
import { errorToast, toast } from "~/ui/admin";

const BANNERS_KEY = ["banners"] as const;
const HUB_BANNER_KEY = ["hub-banner"] as const;

const invalidateBanners = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: BANNERS_KEY });
  queryClient.invalidateQueries({ queryKey: HUB_BANNER_KEY });
};

export const useListBanners = ({
  queryBuilder,
}: {
  queryBuilder?: QueryBuilder<Record<string, string> | undefined>;
}) =>
  useQuery({
    queryKey: [...BANNERS_KEY, queryBuilder?.build() ?? ""],
    queryFn: () => listBanners({ queryBuilder }),
  });

export const useGetBanner = (id: string | undefined) =>
  useQuery({
    queryKey: [...BANNERS_KEY, id],
    queryFn: () => getBanner(id!),
    enabled: !!id,
  });

export const useCreateBanner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBannerPayload) => createBanner(data),
    onSuccess: () => {
      toast({ type: "success", title: "Banner created successfully" });
      invalidateBanners(queryClient);
    },
    onError: errorToast,
  });
};

export const useUpdateBanner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBannerPayload }) =>
      updateBanner(id, data),
    onSuccess: () => {
      toast({ type: "success", title: "Banner updated successfully" });
      invalidateBanners(queryClient);
    },
    onError: errorToast,
  });
};

export const useDeleteBanner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteBanner(id),
    onSuccess: () => {
      toast({ type: "success", title: "Banner deleted successfully" });
      invalidateBanners(queryClient);
    },
    onError: errorToast,
  });
};

export const useToggleBanner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => toggleBanner(id),
    onSuccess: (banner) => {
      toast({
        type: "success",
        title: banner.is_active
          ? "Banner activated successfully"
          : "Banner deactivated successfully",
      });
      invalidateBanners(queryClient);
    },
    onError: errorToast,
  });
};
